import { communicationPreferencesRecordSchema } from "components/communicationPreferences/communicationPreferencesSchema";
import { PersonnelStatus } from "enums/PersonnelEnums";
import { Role } from "enums/Role";
import { phoneFieldSchema } from "validations/PhoneFieldSchema";
import { z } from "zod";

// A contact is a "Group" (e.g. "MFT Dispatch") when Communications Only is the only role
// selected for them — the name is then shown/edited as a single Group Name field instead of
// First Name + Last Name. Adding any other role switches it back to individual name fields.
export const isCommunicationsOnlyGroup = (roles: string[]) =>
	roles.length === 1 && roles[0] === Role.COMMUNICATIONS_ONLY;

// Driver-only contacts can't log in, so email is optional for them.
export const isDriverOnlyRole = (roles: string[]) => roles.length === 1 && roles[0] === Role.DRIVER;

const emailFormatSchema = z.string().trim().email("Enter a valid email address");

// Email requiredness depends on mode and roles, so the schema is built per-use.
export const createUpsertPersonnelSchema = (mode: "add" | "edit" | "duplicate") =>
	z
		.object({
			firstName: z.string().trim().max(50, "Name must be 50 characters or fewer"),
			lastName: z.string().trim().max(50, "Last name must be 50 characters or fewer"),
			email: z.string().trim(),
			phoneNumber: phoneFieldSchema,
			roles: z.array(z.string()).min(1, "Roles are required"),
			status: z.enum(PersonnelStatus, { message: "Status is required" }),
			communicationPreferences: communicationPreferencesRecordSchema
		})
		.superRefine((values, ctx) => {
			const isGroup = isCommunicationsOnlyGroup(values.roles);

			if (!values.firstName.trim()) {
				ctx.addIssue({
					code: "custom",
					message: isGroup ? "Group name is required" : "First name is required",
					path: ["firstName"]
				});
			}
			if (!isGroup && !values.lastName.trim()) {
				ctx.addIssue({ code: "custom", message: "Last name is required", path: ["lastName"] });
			}

			// Edit mode's email field is disabled/immutable, so it's never validated here.
			if (mode !== "edit") {
				if (!values.email) {
					if (!isDriverOnlyRole(values.roles)) {
						ctx.addIssue({ code: "custom", message: "Email is required", path: ["email"] });
					}
				} else if (!emailFormatSchema.safeParse(values.email).success) {
					ctx.addIssue({
						code: "custom",
						message: "Enter a valid email address",
						path: ["email"]
					});
				}
			}
		});

export type AddPersonnelUserFormValues = z.input<ReturnType<typeof createUpsertPersonnelSchema>>;
